import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { filter, take } from 'rxjs/operators';
import {
  IgxCategoryChartModule,
  IgxPieChartModule,
  IgxItemLegendModule,
  IgxLegendModule,
} from 'igniteui-angular-charts';
import { ReportsIntergrationService } from '../../services/reports-intergration.service';
import {
  CompletionTableData,
  MAndEData,
  MandEConfigModel,
  QuizPerformanceResponse,
} from '../../models/studentProfileModels';
import { KeycloakAuthService } from '../../services/keycloak-auth.service';
import { HttpHelperService } from '../../services/helper.service';
import { DateRangeDialogComponent } from '../../date-range-dialog/date-range-dialog.component';
import { ReportsBaseService } from '../../services/reports-base.service';
import { AfricaMapComponent } from '../../africa-map/africa-map.component';
import { ChartJsComponent } from '../../shared/chart-js/chart-js.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TooltipService } from '../../services/tooltip.service';

export type LesediLearningPathId = 'digitalLiteracy' | 'isiZuluDigitalLiteracy';

export interface LesediLearningImpactView {
  usersBeganCourseTotal: number;
  usersBeganCourseChangeClass: string;
  usersBeganCourseChangeIcon: string;
  usersBeganCourseChangePercentage: string;
  courseCompletionsTotal: number;
  courseCompletionsChangeClass: string;
  courseCompletionsChangeIcon: string;
  courseCompletionsChangePercentage: string;
  completionRateTotal: number;
  completionRateChangeClass: string;
  completionRateChangeIcon: string;
  completionRateChangePercentage: string;
  prePostChart: any;
  prePostIndex: { quiz: string; color: string }[];
  prePostOptions: any;
  currentPeriodStats: QuizPerformanceResponse[];
  preQuizChart: any;
  postQuizChart: any;
  engagementChart: any;
  dropOffChart: any;
  financeTable: CompletionTableData[];
  mAndEResponses: MAndEData[];
  mAndEChart: any;
  mAndEConfig: MandEConfigModel[];
  avgImprovement: number;
}

/** Pathways shown on the Feedback & Insight tab (English Pathway + isiZulu Pathway section headings). */
export interface LesediFeedbackInsightPathway {
  label: string;
  pathway: LesediLearningPathId;
}

/** English vs isiZulu counts for the User Language Selection card (Feedback & Insight). */
export interface LesediUserLanguageSelectionBreakdown {
  englishCount: number;
  isiZuluCount: number;
  total: number;
  englishSharePct: number;
  isiZuluSharePct: number;
}

/** One row in the User Language Feedback card (dummy until API exists). */
export interface LesediUserLanguageFeedbackRow {
  label: string;
  count: number;
  /** Share of total learners (0–100); drives bar width. */
  percentage: number;
  /** Second class on the bar fill (e.g. `--excellent`). */
  barFillClass: string;
}

@Component({
  selector: 'app-lesedi-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatDialogModule,
    MatTableModule,
    IgxCategoryChartModule,
    IgxPieChartModule,
    IgxItemLegendModule,
    IgxLegendModule,
    MatTabsModule,
    MatCheckboxModule,
    MatTooltipModule,
    AfricaMapComponent,
    ChartJsComponent,
  ],
  templateUrl: './lesedi-report.component.html',
  styleUrl: './lesedi-report.component.scss',
})
export class LesediReportComponent implements OnInit {

  personalFinanceTableData: CompletionTableData[] = [];
  trend: { percentage: number; color: string } | null = null;

  /**
   * Placeholder learner counts for User Language Selection (no backend yet).
   * Replace with API data when the endpoint exists.
   */
  private readonly lesediUserLanguageSelectionDummyCounts = {
    englishLearners: 342,
    isiZuluLearners: 458,
  };

  /**
   * Placeholder for User Language Feedback (isiZulu Feedback & Insight). Replace when backend exists.
   */
  readonly lesediUserLanguageFeedbackDummy: {
    totalLearners: number;
    rows: LesediUserLanguageFeedbackRow[];
  } = {
    totalLearners: 800,
    rows: [
      {
        label: 'Excellent',
        count: 205,
        percentage: 25.6,
        barFillClass: 'lesedi-language-selection-bar-fill--excellent',
      },
      {
        label: 'Good',
        count: 458,
        percentage: 57.25,
        barFillClass: 'lesedi-language-selection-bar-fill--good',
      },
      {
        label: 'Could be better',
        count: 137,
        percentage: 17.13,
        barFillClass: 'lesedi-language-selection-bar-fill--could-better',
      },
    ],
  };

  readonly lesediFeedbackInsightPathways: LesediFeedbackInsightPathway[] = [
    { label: 'English Pathway', pathway: 'digitalLiteracy' },
    { label: 'isiZulu Pathway', pathway: 'isiZuluDigitalLiteracy' },
  ];

  digitalLiteracyPrePostQuizChartOptions: any = {
    indexAxis: 'y',
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const val = context.parsed.x;
            return `${label}: ${val}%`;
          }
        }
      }
    },
    scales: {
      x: { beginAtZero: true, max: 100, ticks: { callback: (value: any) => value + '%' } },
      y: {
        ticks: {
          autoSkip: false,
          callback: (value: any, index: any) => {
            const config = this.baseService.prePostQuizQuestionsConfig || {};
            const customLabels = config['lesedi_path1'] || [];
            const rawLabel = this.baseService.lesediPath1PrePostChartData?.labels?.[index] as string || '';
            return customLabels[index] || rawLabel || value;
          }
        }
      }
    }
  };

  isiZuluDigitalLiteracyPrePostQuizChartOptions: any = {
    indexAxis: 'y',
    plugins: {
      legend: { display: true, position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const val = context.parsed.x;
            return `${label}: ${val}%`;
          }
        }
      }
    },
    scales: {
      x: { beginAtZero: true, max: 100, ticks: { callback: (value: any) => value + '%' } },
      y: {
        ticks: {
          autoSkip: false,
          callback: (value: any, index: any) => {
            const config = this.baseService.prePostQuizQuestionsConfig || {};
            const customLabels = config['lesedi_path2'] || [];
            const rawLabel = this.baseService.lesediPath2PrePostChartData?.labels?.[index] as string || '';
            return customLabels[index] || rawLabel || value;
          }
        }
      }
    }
  };

  constructor(
    private reportsService: ReportsIntergrationService,
    private authService: KeycloakAuthService,
    private helperService: HttpHelperService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    public baseService: ReportsBaseService,
    public nativeElement: ElementRef,
    public tooltips: TooltipService,
  ) { }

  ngOnInit(): void {
    this.baseService.updateDateRange();
    this.checkUserBotAccess();
  }

  openDateRangeDialog(): void {
    const dialogRef = this.dialog.open(DateRangeDialogComponent, {
      data: { startDate: this.baseService.startDate, endDate: this.baseService.endDate }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.baseService.startDate = result.startDate;
        this.baseService.endDate = result.endDate;
        this.onDateChange();
      }
    });
  }

  getPercentage(count: number): string {
    const total = this.baseService.countryData.reduce((sum, country) => sum + country.count, 0);
    return total === 0 ? '0' : ((count / total) * 100).toFixed(2);
  }

  private checkUserBotAccess(): void {
    this.baseService.isLoading = true;
    this.baseService.error = null;

    const user = this.authService.currentUserValue();
    if (user && user.roles) {
      this.processUserBotAccess(user);
      return;
    }

    this.authService.currentUser$
      .pipe(
        filter((u: any) => !!u && !!u.roles),
        take(1)
      )
      .subscribe({
        next: (u: any) => {
          this.processUserBotAccess(u);
        },
        error: (err) => {
          console.error('Error fetching user data:', err);
          this.baseService.error = "Failed to load user data. Please try logging in again.";
          this.baseService.isLoading = false;
          this.cdr.detectChanges();
        }
      });
  }

  private processUserBotAccess(user: any): void {
    const botRoles = user.roles.filter((role: string) => role.startsWith('bot_access_'));
    this.baseService.availableBots = botRoles.map((role: string) => {
      const botName = role.replace('bot_access_', '');
      return {
        key: botName as 'lwazi' | 'lesedi' | 'kitso' | 'kitsoecd' | 'kitsozazi' | 'kitsoai' | 'mpilo',
        name: botName.charAt(0).toUpperCase() + botName.slice(1)
      };
    });

    this.baseService.availableBots = this.baseService.availableBots.filter(bot => bot.key === 'lesedi');

    if (this.baseService.availableBots.length > 0) {
      this.baseService.selectedBot = 'lesedi';
      this.loadAllData();
    } else {
      this.baseService.error = "You do not have access to Lesedi bot. Please contact an administrator.";
    }
    this.baseService.isLoading = false;
    this.cdr.detectChanges();
  }

  onDateChange(): void {
    this.baseService.clearData();
    this.baseService.updateDateRange();
    if (this.baseService.selectedBot) {
      this.loadAllData();
    }
  }

  onProvinceFilterChange(): void {
    this.baseService.updateProvinceChartForFilter();
    this.cdr.detectChanges();
  }

  onLanguageFilterChange(): void {
    this.baseService.updateLanguageChartForFilter();
    this.cdr.detectChanges();
  }

  resolveLesediLearningImpactView(pathway: LesediLearningPathId): LesediLearningImpactView {
    const reportsBaseService = this.baseService;
    if (pathway === 'digitalLiteracy') {
      return {
        usersBeganCourseTotal: reportsBaseService.lesediPath1UsersBeganCourseTotal,
        usersBeganCourseChangeClass: reportsBaseService.lesediPath1UsersBeganCourseChangeClass,
        usersBeganCourseChangeIcon: reportsBaseService.lesediPath1UsersBeganCourseChangeIcon,
        usersBeganCourseChangePercentage: reportsBaseService.lesediPath1UsersBeganCourseChangePercentage,
        courseCompletionsTotal: reportsBaseService.lesediPath1CourseCompletionsTotal,
        courseCompletionsChangeClass: reportsBaseService.lesediPath1CourseCompletionsChangeClass,
        courseCompletionsChangeIcon: reportsBaseService.lesediPath1CourseCompletionsChangeIcon,
        courseCompletionsChangePercentage: reportsBaseService.lesediPath1CourseCompletionsChangePercentage,
        completionRateTotal: reportsBaseService.lesediPath1CompletionRateTotal,
        completionRateChangeClass: reportsBaseService.lesediPath1CompletionRateChangeClass,
        completionRateChangeIcon: reportsBaseService.lesediPath1CompletionRateChangeIcon,
        completionRateChangePercentage: reportsBaseService.lesediPath1CompletionRateChangePercentage,
        prePostChart: reportsBaseService.lesediPath1PrePostChartData,
        prePostIndex: reportsBaseService.lesediPath1PrePostQuizQuestionsIndexData,
        prePostOptions: this.digitalLiteracyPrePostQuizChartOptions,
        currentPeriodStats: reportsBaseService.lesediPath1CurrentPeriodStats,
        preQuizChart: reportsBaseService.lesediPath1PreQuizScoresChartData,
        postQuizChart: reportsBaseService.lesediPath1PostQuizScoresChartData,
        engagementChart: reportsBaseService.lesediPath1OverAllEngagementChartData,
        dropOffChart: reportsBaseService.lesediPath1DropOffChartData,
        financeTable: reportsBaseService.personalFinanceTableData,
        mAndEResponses: reportsBaseService.lesediPath1MAndEResponses,
        mAndEChart: reportsBaseService.lesediPath1MAndEChart,
        mAndEConfig: reportsBaseService.lesediPath1MAndEConfigArray,
        avgImprovement: reportsBaseService.getAverageImprovement(reportsBaseService.lesediPath1CurrentPeriodStats),
      };
    }
    return {
      usersBeganCourseTotal: reportsBaseService.lesediPath2UsersBeganCourseTotal,
      usersBeganCourseChangeClass: reportsBaseService.lesediPath2UsersBeganCourseChangeClass,
      usersBeganCourseChangeIcon: reportsBaseService.lesediPath2UsersBeganCourseChangeIcon,
      usersBeganCourseChangePercentage: reportsBaseService.lesediPath2UsersBeganCourseChangePercentage,
      courseCompletionsTotal: reportsBaseService.lesediPath2CourseCompletionsTotal,
      courseCompletionsChangeClass: reportsBaseService.lesediPath2CourseCompletionsChangeClass,
      courseCompletionsChangeIcon: reportsBaseService.lesediPath2CourseCompletionsChangeIcon,
      courseCompletionsChangePercentage: reportsBaseService.lesediPath2CourseCompletionsChangePercentage,
      completionRateTotal: reportsBaseService.lesediPath2CompletionRateTotal,
      completionRateChangeClass: reportsBaseService.lesediPath2CompletionRateChangeClass,
      completionRateChangeIcon: reportsBaseService.lesediPath2CompletionRateChangeIcon,
      completionRateChangePercentage: reportsBaseService.lesediPath2CompletionRateChangePercentage,
      prePostChart: reportsBaseService.lesediPath2PrePostChartData,
      prePostIndex: reportsBaseService.lesediPath2PrePostQuizQuestionsIndexData,
      prePostOptions: this.isiZuluDigitalLiteracyPrePostQuizChartOptions,
      currentPeriodStats: reportsBaseService.lesediPath2CurrentPeriodStats,
      preQuizChart: reportsBaseService.lesediPath2PreQuizScoresChartData,
      postQuizChart: reportsBaseService.lesediPath2PostQuizScoresChartData,
      engagementChart: reportsBaseService.lesediPath2OverAllEngagementChartData,
      dropOffChart: reportsBaseService.lesediPath2DropOffChartData,
      financeTable: reportsBaseService.businessFinanceTableData,
      mAndEResponses: reportsBaseService.lesediPath2MAndEResponses,
      mAndEChart: reportsBaseService.lesediPath2MAndEChart,
      mAndEConfig: reportsBaseService.lesediPath2MAndEConfigArray,
      avgImprovement: reportsBaseService.getAverageImprovement(reportsBaseService.lesediPath2CurrentPeriodStats),
    };
  }

  /**
   * User Language Selection card: dummy counts until backend is wired; bar widths use share of the two-way total.
   */
  get lesediUserLanguageSelectionBreakdown(): LesediUserLanguageSelectionBreakdown {
    const englishCount = this.lesediUserLanguageSelectionDummyCounts.englishLearners;
    const isiZuluCount = this.lesediUserLanguageSelectionDummyCounts.isiZuluLearners;
    const total = englishCount + isiZuluCount;
    const englishSharePct = total === 0 ? 0 : (englishCount / total) * 100;
    const isiZuluSharePct = total === 0 ? 0 : (isiZuluCount / total) * 100;
    return {
      englishCount,
      isiZuluCount,
      total,
      englishSharePct,
      isiZuluSharePct,
    };
  }

  private generateLesediPrePostChartConfig(stats: QuizPerformanceResponse[] | null | undefined) {
    if (!stats || stats.length === 0) return null;
    const questions = stats.map(q => q.quizQuestion);
    const preData = stats.map(q => parseFloat(q.pre_learning_percentage) || 0);
    const postData = stats.map(q => parseFloat(q.post_learning_percentage) || 0);
    const preColor = 'rgba(235, 182, 6, 1)';
    const postColor = 'rgba(62, 132, 220, 1)';
    return {
      labels: questions,
      datasets: [
        {
          label: 'Pre-Learning',
          data: preData,
          backgroundColor: Array(preData.length).fill(preColor),
          borderColor: Array(preData.length).fill(preColor),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: this.baseService.prePostQuizBarThicknessPx,
        },
        {
          label: 'Post-Learning',
          data: postData,
          backgroundColor: Array(postData.length).fill(postColor),
          borderColor: Array(postData.length).fill(postColor),
          borderWidth: 1,
          borderRadius: 4,
          borderSkipped: false,
          barThickness: this.baseService.prePostQuizBarThicknessPx,
        }
      ]
    };
  }

  private buildPrePostQuizChartData() {
    const preColor = 'rgba(235, 182, 6, 1)';
    const postColor = 'rgba(62, 132, 220, 1)';
    const legend = [
      { quiz: 'Pre-Learning', color: preColor },
      { quiz: 'Post-Learning', color: postColor },
    ];

    this.baseService.lesediPath1PrePostChartData = this.generateLesediPrePostChartConfig(
      this.baseService.lesediPath1CurrentPeriodStats
    );
    this.baseService.lesediPath1PrePostQuizQuestionsIndexData = this.baseService.lesediPath1PrePostChartData
      ? legend
      : [];

    this.baseService.lesediPath2PrePostChartData = this.generateLesediPrePostChartConfig(
      this.baseService.lesediPath2CurrentPeriodStats
    );
    this.baseService.lesediPath2PrePostQuizQuestionsIndexData = this.baseService.lesediPath2PrePostChartData
      ? legend
      : [];
  }

  private createDoughnutConfig(value: number, colorKey: 'preQuiz' | 'postQuiz' | 'overAllEngagement', label: string) {
    const tone = this.baseService.pieChartColor[colorKey];
    const fill = Array.isArray(tone) ? tone[0] : tone;
    return {
      labels: [label, 'Remaining'],
      datasets: [{
        data: [value, 100 - value],
        backgroundColor: [fill, '#E0E0E0'],
        label
      }]
    };
  }

  private buildLesediAssessmentCharts(): void {
    const reportsBaseService = this.baseService;

    const buildPathwayAssessmentCharts = (stats: QuizPerformanceResponse[], beganCourse: number) => {
      const pre = reportsBaseService.getAveragePreQuizScore(stats);
      const post = reportsBaseService.getAveragePostQuizScore(stats);
      const drop = reportsBaseService.calculateDropOffStats(stats, beganCourse);
      const engagement = reportsBaseService.getOverallEngagementStats(beganCourse, reportsBaseService.enrolledUsersTotal);
      return {
        preQuiz: this.createDoughnutConfig(pre, 'preQuiz', 'Average Pre-Quiz Score'),
        postQuiz: this.createDoughnutConfig(post, 'postQuiz', 'Average Post-Quiz Score'),
        engagement: this.createDoughnutConfig(engagement, 'overAllEngagement', 'Average Engagement Score'),
        dropOff: this.createDoughnutConfig(drop, 'overAllEngagement', 'Drop-Off Percentage'),
      };
    };

    const digitalLiteracyAssessmentCharts = buildPathwayAssessmentCharts(
      reportsBaseService.lesediPath1CurrentPeriodStats,
      reportsBaseService.lesediPath1UsersBeganCourseTotal
    );
    reportsBaseService.lesediPath1PreQuizScoresChartData = digitalLiteracyAssessmentCharts.preQuiz;
    reportsBaseService.lesediPath1PostQuizScoresChartData = digitalLiteracyAssessmentCharts.postQuiz;
    reportsBaseService.lesediPath1OverAllEngagementChartData = digitalLiteracyAssessmentCharts.engagement;
    reportsBaseService.lesediPath1DropOffChartData = digitalLiteracyAssessmentCharts.dropOff;

    const isiZuluDigitalLiteracyAssessmentCharts = buildPathwayAssessmentCharts(
      reportsBaseService.lesediPath2CurrentPeriodStats,
      reportsBaseService.lesediPath2UsersBeganCourseTotal
    );
    reportsBaseService.lesediPath2PreQuizScoresChartData = isiZuluDigitalLiteracyAssessmentCharts.preQuiz;
    reportsBaseService.lesediPath2PostQuizScoresChartData = isiZuluDigitalLiteracyAssessmentCharts.postQuiz;
    reportsBaseService.lesediPath2OverAllEngagementChartData = isiZuluDigitalLiteracyAssessmentCharts.engagement;
    reportsBaseService.lesediPath2DropOffChartData = isiZuluDigitalLiteracyAssessmentCharts.dropOff;
  }

  private buildLesediMAndEChartData(pathway: LesediLearningPathId): void {
    const rawData =
      pathway === 'digitalLiteracy'
        ? this.baseService.lesediPath1MAndEAverages
        : this.baseService.lesediPath2MAndEAverages;
    const chartConfig = this.baseService.buildFeedbackRatingOverTimeChartData(rawData);
    if (pathway === 'digitalLiteracy') {
      this.baseService.lesediPath1MAndEChart = chartConfig;
    } else {
      this.baseService.lesediPath2MAndEChart = chartConfig;
    }
  }

  private loadAllData(): Promise<void> {
    const localBot = this.baseService.selectedBot;
    if (!localBot) return Promise.resolve();

    this.baseService.isLoading = true;
    const currentStart = this.baseService.formatDate(this.baseService.startDate);
    const currentEnd = this.baseService.formatDate(this.baseService.endDate);
    const { prevStart, prevEnd } = this.baseService.getPreviousPeriod(this.baseService.startDate, this.baseService.endDate);

    const quizStats = this.baseService.loadPrePostQuizStats(localBot, currentStart, currentEnd);
    const engagement = this.baseService.loadEngagementCount(localBot, currentStart, currentEnd, prevStart, prevEnd);
    const completion = this.baseService.loadCompletionStats(localBot, currentStart, currentEnd, prevStart, prevEnd);
    const mAndEStats = this.baseService.loadMAndEStats(localBot, currentStart, currentEnd);
    const mAndENumber = this.baseService.loadMAndENumber(localBot, currentStart, currentEnd);

    return Promise.all([
      this.baseService.loadRemainingData(currentStart, currentEnd),
      this.baseService.loadBreakdowns(localBot, currentStart, currentEnd),
      this.baseService.loadTables(localBot, currentStart, currentEnd, prevStart, prevEnd),

      quizStats.then(() => {
        this.buildPrePostQuizChartData();
        this.cdr.detectChanges();
      }),

      Promise.all([quizStats, engagement, completion]).then(() => {
        this.buildLesediAssessmentCharts();
        this.cdr.detectChanges();
      }),

      Promise.all([mAndEStats, mAndENumber]).then(() => {
        this.baseService.updateMAndEConfig();
        this.buildLesediMAndEChartData('digitalLiteracy');
        this.buildLesediMAndEChartData('isiZuluDigitalLiteracy');
        this.cdr.detectChanges();
      }),
    ])
    .then(() => {
      this.baseService.isLoading = false;
      this.cdr.markForCheck();
    })
    .catch((error) => {
      console.error('Error in loadAllData chain:', error);
      this.baseService.isLoading = false;
      this.baseService.error = 'Failed to load report data';
      this.cdr.detectChanges();
    });
  }

  private clearData(): void {
    this.baseService.clearData();
  }
}
